import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { Mail, ArrowLeft, CheckCircle2 } from "lucide-react";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen auth-bg grid place-items-center p-6 sm:p-10">
      <Card className="w-full max-w-md border-border shadow-none" data-testid="forgot-password-card">
        <CardContent className="p-8">
          <div className="tiny-label">Account recovery</div>
          <h2 className="font-display text-2xl font-bold mt-2">Forgot password?</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Enter the email on your HR Track account and we'll send you a reset link.
          </p>

          {sent ? (
            <div className="mt-8 space-y-4 text-sm" data-testid="forgot-password-sent">
              <div className="flex items-start gap-3 p-4 rounded-md bg-emerald-50 border border-emerald-200">
                <CheckCircle2 className="w-5 h-5 text-emerald-700 mt-0.5 shrink-0" />
                <div className="text-emerald-900">
                  If <span className="font-medium">{email}</span> is registered, a password reset link is on its way.
                  The link expires in <span className="font-medium">60 minutes</span>.
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Don't see it? Check your spam folder. Still nothing? Contact your HR administrator.
              </p>
              <Link to="/login" className="inline-flex items-center text-sm font-medium underline">
                <ArrowLeft className="w-3.5 h-3.5 mr-1.5" /> Back to sign in
              </Link>
            </div>
          ) : (
            <form className="mt-8 space-y-5" onSubmit={submit}>
              <div>
                <Label htmlFor="email">Work email</Label>
                <div className="relative mt-1.5">
                  <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-11 pl-9"
                    placeholder="you@company.com"
                    data-testid="forgot-email-input"
                  />
                </div>
              </div>
              <Button type="submit" disabled={submitting} className="w-full h-11" data-testid="forgot-submit-btn">
                {submitting ? "Sending…" : "Send reset link"}
              </Button>
              <Link to="/login" className="inline-flex items-center text-xs text-muted-foreground hover:text-foreground">
                <ArrowLeft className="w-3 h-3 mr-1" /> Back to sign in
              </Link>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
